import { NGXLogger } from 'ngx-logger';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AppConstants } from '../commons/app-constants';
import { EncryptionService } from './encryption.service';
import { BehaviorSubject, Observable, Subject, from, of, throwError, timer } from 'rxjs';
import { catchError, retry, switchMap, tap } from 'rxjs/operators';
import { offlineDb, scopedKey, touchCachedFile } from '../offline/offline-db';
import { isOfflineCapableNative } from '../commons/utils/platform';

@Injectable({
  providedIn: 'root'
})
export class SideBarMenuService {
  private readonly logger = inject(NGXLogger);


  // private baseUrl = `${environment.httpProtocol}${environment.ipaddress}${environment.port}`;

  private baseUrl = `${AppConstants.API_URL}`;

  private showMenuSubject = new BehaviorSubject<boolean>(true);
  showMenu$ = this.showMenuSubject.asObservable();

  private showMenuButtonSubject = new BehaviorSubject<boolean>(true);
  showMenuButton$ = this.showMenuButtonSubject.asObservable();

  /** The "+ New" entries this user may use. The sidebar works them out (rights + menu) and publishes them here, so the header's "+ New" button shows exactly the same list without any server call. */
  private newEntriesSubject = new BehaviorSubject<{ labelKey: string; icon: string; link: string }[]>([]);
  newEntries$ = this.newEntriesSubject.asObservable();
  setNewEntries(entries: { labelKey: string; icon: string; link: string }[]) {
    this.newEntriesSubject.next(entries);
  }

  private refreshMenuSubject = new BehaviorSubject<number>(0);
  refreshMenu$ = this.refreshMenuSubject.asObservable();

  setShowMenu(value: boolean) {
    this.showMenuSubject.next(value);
  }

  setShowMenuButton(value: boolean) {
    this.showMenuButtonSubject.next(value);
  }

  requestMenuRefresh() {
    this.refreshMenuSubject.next(Date.now());
  }


  constructor(private http: HttpClient,
    private encryptionService: EncryptionService
  ) { }

  // Write-through cache: whatever menu the server last returned is stashed locally, so a cold
  // app open with no network (or a navigation while offline) still shows the sidebar instead
  // of it silently going blank — SideBarComponent.loadMenuItems() treats any error (including
  // a network failure) as "no items". Not staleness-tracked in the UI on purpose: this is
  // navigation chrome, not data the user needs to know is out of date.
  private static readonly MENU_CACHE_KEY = 'sideBarMenu';

  /**
   * Tenant-scoped cache key, via the same scopedKey() helper every other offline cache uses.
   *
   * The menu used to be cached under the bare key above. It is read back on ANY fetch error --
   * offline, slow, or the 401 during a login transition -- so on a shared device (log out as one
   * user, log in as another) it served whoever cached last. Reproduced live on iOS: an account
   * holding only REGISTRATION and ADMIN was shown SUPER_ADMIN/ROOT/CRM/Affiliate entries from the
   * previous session. They did not work -- the backend checks every call -- but they were visible.
   *
   * This service reached past MasterCacheService straight to offlineDb, which is why it never got
   * the scoping the shared cache layers already had.
   *
   * Returns null when no tenant marker exists yet, and the caller then skips the cache entirely.
   * scopedKey() itself falls back to the raw key in that window by design; for navigation chrome
   * an empty menu is a safe failure and another tenant's menu is not, so this one does not take
   * that fallback.
   */
  private menuCacheKey(): string | null {
    return localStorage.getItem(AppConstants.LAST_DEVICE_TENANT_ID)
      ? scopedKey(SideBarMenuService.MENU_CACHE_KEY)
      : null;
  }

  getSideBarMenu(forceRefresh: boolean = false): Observable<any> {
    const cacheBuster = forceRefresh ? `?_ts=${Date.now()}` : '';
    const url = `${AppConstants.API_URL}api/v1/menu-items${cacheBuster}`;
    this.logger.debug(`Url for getting menu ${url}`)
    const cacheKey = this.menuCacheKey();
    return this.http.get(url).pipe(
      tap((data) => {
        if (isOfflineCapableNative() && cacheKey) {
          offlineDb.masterCache.put({ key: cacheKey, data, fetchedAt: Date.now() });
          // One-time cleanup of the pre-fix shared entry, so a device that already has a menu
          // cached under the old global key cannot serve it to anyone.
          offlineDb.masterCache.delete(SideBarMenuService.MENU_CACHE_KEY).catch(() => {});
        }
      }),
      catchError((err) => (isOfflineCapableNative() && cacheKey
        ? from(offlineDb.masterCache.get(cacheKey)).pipe(
            // A saved copy of THIS user's menu is a fine stand-in while offline. No saved copy is NOT "an empty
            // menu": pass the failure on, so the sidebar shows its retry state instead of a blank menu that looks
            // final (an empty list here used to be reported as a successful load, and was never tried again).
            switchMap((entry) => (entry?.data ? of(entry.data) : throwError(() => err))),
          )
        : throwError(() => err))),
    );
  }

  getUserProfile() {
    // alert(`Sidebarmenu service :: User Profile`)
    const url = `${AppConstants.API_URL}api/v1/user-profile`;
    this.logger.debug(`Url for getting user profile ${url}`)
    return this.http.get(url);
  }

  /**
   * Same request as getUserProfile(), but a short hiccup (no connection for a moment, a 5xx while the server is
   * busy — typical right after login or signup) is tried again up to 3 times, 1.5 s / 3 s / 6 s apart. Used by the
   * header and sidebar, whose profile load used to give up silently on the first failure and leave the avatar
   * initial / company "-" until the next app start. A real answer (401, 403, 404...) is NOT retried, and nothing is
   * retried while the phone is offline. Deliberately separate from getUserProfile(): route guards wait on that one
   * and must not be delayed by retries.
   */
  getUserProfileWithRetry(): Observable<any> {
    return this.getUserProfile().pipe(
      retry({
        count: 3,
        delay: (error: any, attempt: number) => {
          const status = error?.status;
          const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
          if (offline || (status !== 0 && !(status >= 500))) return throwError(() => error);
          return timer(1500 * Math.pow(2, attempt - 1));
        },
      }),
    );
  }

  async setEncryptedUserProfile(userProfile: any) {
    try {
      const userProfileString = JSON.stringify(userProfile);
      //      console.log(`Plain User Profile {}`,userProfile);
      const encryptedData = await this.encryptionService.encrypt(userProfileString);
      localStorage.setItem(AppConstants.USER_PROFILE_KEY, encryptedData);
      // console.log("Encrypted Profile:", encryptedData);
    } catch (error) {
      this.logger.error('Error encrypting user profile:', error);
    }
    this.cacheAvatarBlob(userProfile);
  }

  // Called after every successful live profile fetch (both the initial load and the
  // avatar-updated round-trip). userProfile.profilePicture itself is a signed Backblaze B2 URL
  // (or, for a social-login avatar, a raw external OAuth-provider URL) — a direct HttpClient
  // fetch of either would get the global interceptor's unconditional withCredentials:true
  // attached to a cross-origin request, which gets CORS-rejected. The /profile/picture/content
  // endpoint re-fetches server-side instead (not subject to browser CORS) and streams the bytes
  // back same-origin, same fix shape as ProductService.cachePhotoBlob.
  /**
   * The saved profile picture is kept PER USER (2026-09-30). It used to live under one fixed key, so after one user
   * was signed out and another signed in, the phone could show the previous person's photo (it is what the header and
   * sidebar fall back to whenever the live picture fails to load or the phone is offline). Keyed by the "last user on
   * this device" email, which AuthSessionGuard records before the header/sidebar load. No email yet = nothing is
   * saved or shown.
   */
  private avatarKey(): string | null {
    const email = localStorage.getItem(AppConstants.LAST_DEVICE_USER_EMAIL);
    return email ? `userAvatar:${email.toLowerCase()}` : null;
  }

  private cacheAvatarBlob(userProfile: any): void {
    const avatarKey = this.avatarKey();
    if (!isOfflineCapableNative() || !userProfile?.profilePicture || !avatarKey) return;
    this.http.get(`${AppConstants.API_URL}api/v1/profile/picture/content`, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        // Only a real picture file is kept (2026-09-29): on the iPhone app this request goes through the
        // native network layer — anything that isn't a non-empty Blob would never show offline anyway.
        if (!(blob instanceof Blob) || !blob.size) {
          console.warn('[offline] profile picture not saved — unexpected response', typeof blob);
          return;
        }
        // Still the same user? (a slow download must not save one person's photo under another's name)
        if (this.avatarKey() !== avatarKey) return;
        offlineDb.detailCache.put({ key: avatarKey, data: blob, fetchedAt: Date.now() })
          .then(() => {
            offlineDb.detailCache.delete('userAvatar').catch(() => undefined); // old shared key (pre-fix)
            this.avatarCachedSubject.next();
          })
          .catch(() => undefined);
      },
      error: () => {},
    });
  }

  /** Fires once the profile picture has been saved for offline use (2026-09-29) — the header, sidebar
   *  and chat re-read their offline copy then. Before, they read it only once at start-up, which on a
   *  fresh start was BEFORE it was saved, so the picture vanished in Airplane mode. */
  private readonly avatarCachedSubject = new Subject<void>();
  readonly avatarCached$ = this.avatarCachedSubject.asObservable();

  // Consumed by HeaderComponent/SideBarComponent — each holds its own in-memory userProfile
  // copy (see avatarUpdated$'s own comment above), so each calls this independently rather than
  // sharing one cached object-URL.
  async getCachedAvatarUrl(): Promise<string | null> {
    if (!isOfflineCapableNative()) return null;
    const avatarKey = this.avatarKey();
    if (!avatarKey) return null;
    const entry = await offlineDb.detailCache.get(avatarKey);
    touchCachedFile(entry); // keep this file through the offline file clean-up (Phase 2)
    return entry?.data instanceof Blob ? URL.createObjectURL(entry.data) : null;
  }

  async getDecryptedUserProfile(): Promise<any> {
    let attempts = 0;
    const maxAttempts = 1;
    const retryDelay = 3000; // 3 seconds

    while (attempts < maxAttempts) {
      try {
        const encryptedProfile = localStorage.getItem(AppConstants.USER_PROFILE_KEY);
        if (encryptedProfile) {
          const decryptedData = await this.encryptionService.decrypt(encryptedProfile);
          const userProfile = JSON.parse(decryptedData);
          this.logger.debug("Decrypted Profile:", userProfile);
          return userProfile;
        }
        return null;
      } catch (error) {
        attempts++;
        this.logger.warn(`Error decrypting user profile (attempt ${attempts}) — clearing stale cache:`, error);
        localStorage.removeItem(AppConstants.USER_PROFILE_KEY);
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        } else {
          return null;
        }
      }
    }
  }


  private avatarClickableSubject = new BehaviorSubject<boolean>(true);
  avatarClickable$ = this.avatarClickableSubject.asObservable();

  setAvatarClickable(value: boolean) {
    this.avatarClickableSubject.next(value);
  }

  // Fired by AvatarUploadComponent after a successful upload/remove so the header and
  // sidebar (which each hold their own decrypted-cache copy of userProfile in memory,
  // not a live stream) can patch just the profilePicture field without a full page reload.
  private avatarUpdatedSubject = new Subject<string | null>();
  avatarUpdated$ = this.avatarUpdatedSubject.asObservable();

  notifyAvatarUpdated(profilePicture: string | null) {
    this.avatarUpdatedSubject.next(profilePicture);
  }

  // Same reasoning as avatarUpdated$ above, for CompanyBrandingComponent's logo save/upload/reset
  // — the header's own in-memory userProfile copy needs patching without a full page reload.
  private companyLogoUpdatedSubject = new Subject<string | null>();
  companyLogoUpdated$ = this.companyLogoUpdatedSubject.asObservable();

  notifyCompanyLogoUpdated(companyLogoUrl: string | null) {
    this.companyLogoUpdatedSubject.next(companyLogoUrl);
  }
}
